import {getCookie, setCookie} from '@/utils/main';
import {
  SoopButtonCommon,
  SoopCheckbox,
  useDialog,
  useDialogContext,
} from '@soop-ui/soop-ui';
import {useCallback, useEffect, useRef, useState} from 'react';
import styles from './NoticeLayer.module.scss';
import {matchPath, useLocation} from 'react-router-dom';

interface ContentProps {
  checked: boolean;
  onCheck: () => void;
  onClose?: () => void;
}

interface NoticeLayerProps {
  // 팝업창 관련 설정
  /** 팝업 ID */
  id: string;
  /** N일간 보지 않기 */
  hideForDays?: number;
  /** 공통 팝업 UI 숨기기 */
  hideDefaultUi?: boolean;
  /**
   * 팝업 노출 허용 경로 (React 외부에 상수 배열 형태로 선언 후 넣어주세요.)
   *
   * Note. allowPath를 지정했더라도 해당 페이지의 컴포넌트 본인 혹은 부모가 useNoticeLayer 호출을 포함해야 합니다.
   */
  allowPaths?: string[];
  onClose?: () => void;
  // 배너 관련 정보
  /** 팝업 배너 내용 */
  content: React.ReactNode | ((props: ContentProps) => React.ReactNode);
  /** 노출 시작일 */
  startDate?: string;
  /** 노출 종료일 */
  endDate?: string;
}

const NoticeLayerContainer = ({
  id,
  content,
  hideForDays,
  hideDefaultUi,
  onClose,
}: Omit<NoticeLayerProps, 'startDate' | 'endDate'>) => {
  const {close} = useDialogContext();
  const [checked, setChecked] = useState(false);

  const handleCheck = () => setChecked((prev) => !prev);
  const handleClose = () => {
    onClose?.();
    // 사용자가 닫았다면 현재 세션에선 비활성화
    sessionStorage.setItem(id, 'y');
    if (checked) {
      setCookie(id, 'y', undefined, hideForDays);
    }
    close();
  };

  return (
    <div className={styles.layerContainer}>
      <div className={styles.content}>
        {typeof content === 'function'
          ? content({checked, onCheck: handleCheck, onClose: handleClose})
          : content}
      </div>
      {!hideDefaultUi && (
        <div className={styles.defaultUi}>
          <SoopCheckbox
            id={`checkbox-${id}`}
            checked={checked}
            onChange={handleCheck}
            label={`${hideForDays}일간 다시 보지 않기`}
          />
          <SoopButtonCommon onClick={handleClose} variant='lineSecondary'>
            닫기
          </SoopButtonCommon>
        </div>
      )}
    </div>
  );
};

/**
 * SOOP LIVE 공용 이벤트 팝업 노출 훅
 */
export const useNoticeLayer = ({
  id,
  hideForDays = 1,
  hideDefaultUi,
  allowPaths,
  onClose,
}: Omit<NoticeLayerProps, 'content' | 'startDate' | 'endDate'>) => {
  const {openDialog, closeDialog} = useDialog();

  /** 레이어 열기 함수를 재사용하기 위한 Ref */
  const innerOpenerRef = useRef<() => void>(null);

  /**
   * 팝업을 노출합니다.
   */
  const showNoticeLayer = useCallback(
    ({
      content,
      startDate,
      endDate,
    }: Pick<NoticeLayerProps, 'content' | 'startDate' | 'endDate'>) => {
      // Note. 레이어 노출 함수를 Ref에 저장합니다.
      innerOpenerRef.current = () => {
        const isLayerHidden = getCookie(id) === 'y';
        if (isLayerHidden) {
          // N일간 다시보지 않기 설정되어있음
          return;
        }

        const isUserClosed = sessionStorage.getItem(id) === 'y';
        if (isUserClosed) {
          // 현재 세션에서 사용자가 한 번 닫았음
          return;
        }

        const now = new Date();
        if (
          !(
            (!startDate || new Date(startDate) <= now) &&
            (!endDate || now <= new Date(endDate))
          )
        ) {
          // 노출 기한 벗어남
          return;
        }

        openDialog({
          component: () => {
            return (
              <NoticeLayerContainer
                id={id}
                content={content}
                hideForDays={hideForDays}
                hideDefaultUi={hideDefaultUi}
                onClose={onClose}
              />
            );
          },
          displayType: 'bottomRight',
          dimmed: false,
          ignoreOutsideClick: true,
        });
      };
      innerOpenerRef.current();
    },
    [hideDefaultUi, hideForDays, id, onClose, openDialog],
  );

  const closeNoticeLayer = useCallback(() => {
    closeDialog(false);
  }, [closeDialog]);

  const {pathname} = useLocation();
  /**
   * 현재 경로와 제공된 allowPaths를 비교해 허용 경로 설정에 따라 팝업을 열고 닫습니다.
   */
  useEffect(() => {
    if (!allowPaths) {
      return;
    }

    const isAllowedPath = allowPaths.some((path) => matchPath(path, pathname));

    if (!isAllowedPath) {
      closeNoticeLayer();
    } else {
      innerOpenerRef.current?.();
    }
  }, [allowPaths, pathname, closeNoticeLayer]);

  return {showNoticeLayer, closeNoticeLayer};
};
